/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  AutocompleteContextualMenu component
 */

import { Box } from '@chakra-ui/react'
import { selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import type { AutocompleteApiResult, AutocompleteCurrentPositionResult } from '@zcode/bizos'
import { createAllowedPropsAreEqual } from '@zcode/react-utils'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getCurrentPosition } from '../../../../shared/components/Autocomplete/module/thunks'
import { trackExitClick } from '../../../../shared/components/Autocomplete/module/thunks/analytics'
import type * as Api from '../../../api/index'
import { cn } from '../../../utils/css'
import { useAutocompleteContext } from '../AutocompleteContext'
import { AutocompleteFormContainer } from '../AutocompleteForm/AutocompleteFormContainer'
import { AutocompleteMobileBackButton } from '../AutocompleteForm/AutocompleteMobileBackButton'
import type { AutocompleteOrigin, AutocompleteTheme } from '../constants'
import { getIdFromOrigin } from '../helpers'
import { AutocompleteResults } from './AutocompleteResults'

export interface AutocompleteContextualMenuProps {
  children?: React.ReactNode
  dispatchMixpanelAutocompleteResultClick?: ({
    result,
    index,
  }: {
    result: AutocompleteApiResult
    index: number
  }) => void
  distanceFromTop: number | undefined
  exclude: Parameters<(typeof Api)['autocompleteResults']['query']>[0]['exclude']
  fillInputOnSelect?: boolean
  forceContextualMenuOpen?: boolean
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  hideMobileAutocomplete?: () => void
  isMapListNav: boolean | undefined
  isShortTerm?: boolean
  onError?: (error: { message: string }) => void
  onSelectAutocompleteResult: (
    result: AutocompleteApiResult | AutocompleteCurrentPositionResult
  ) => void
  origin?: AutocompleteOrigin
  placeholder?: string
  resetForm: () => void
  searchTerm: string
  setFieldValue: (field: 'searchTerm', value: string, shouldValidate?: boolean | undefined) => void // from formik
  sholdNotFocusForMobile?: boolean
  showMobileAutocomplete?: boolean
  theme: AutocompleteTheme
}

const AutocompleteContextualMenuComponent = ({
  children,
  distanceFromTop = 0,
  exclude,
  fillInputOnSelect,
  forceContextualMenuOpen: forceContextualMenuOpenProp,
  handleChange,
  hideMobileAutocomplete,
  isMapListNav,
  isShortTerm,
  dispatchMixpanelAutocompleteResultClick,
  onError,
  onSelectAutocompleteResult,
  origin,
  placeholder,
  resetForm,
  searchTerm: formSearchTerm,
  setFieldValue,
  sholdNotFocusForMobile,
  showMobileAutocomplete,
  theme,
}: AutocompleteContextualMenuProps) => {
  const dispatch = useDispatch()
  const {
    isFocused,
    predictionResults,
    forceContextualMenuOpen: forceContextualMenuOpenContext,
    keystrokes,
    searchTerm,
    setIsFocused,
    setForceContextualMenuOpen,
    setPredictionResults,
    setSearchTerm,
    setKeystrokes,
  } = useAutocompleteContext()

  const isMobile = useSelector(selectIsMobile)

  const forceContextualMenuOpen = forceContextualMenuOpenProp ?? forceContextualMenuOpenContext

  const onClickGetCurrentPosition = () => {
    dispatch(getCurrentPosition({ onSelectAutocompleteResult, onError, origin }))
  }

  const handleClickLink = () => {
    setForceContextualMenuOpen(false)
    resetForm()
    setTimeout(() => {
      hideMobileAutocomplete?.()
      setPredictionResults(null)
      setSearchTerm('')
      setKeystrokes(0)
    })
  }

  const onClickBack = () => {
    hideMobileAutocomplete?.()
    trackExitClick({ origin, keystrokes, predictionResults, searchTerm, setKeystrokes })
  }
  const onMouseDown = () => setForceContextualMenuOpen(true)
  const onMouseEnter = () => setForceContextualMenuOpen(true)
  const onMouseLeave = () => setForceContextualMenuOpen(false)
  const onMouseUpResultLink = (e: React.MouseEvent<HTMLDivElement>) =>
    e.currentTarget.className.includes('Autocomplete_inputBtnsContainer')
      ? handleClickLink()
      : !isMobile && setIsFocused(true)
  const onMouseUpFooterLink = (e: React.MouseEvent<HTMLDivElement>) =>
    'onclick' in e.target && e.target.onclick ? handleClickLink() : !isMobile && setIsFocused(true)

  const shouldShowForShortTerm = isFocused || forceContextualMenuOpen
  const shouldShow =
    isShortTerm && !isMobile
      ? shouldShowForShortTerm
      : isFocused || showMobileAutocomplete || forceContextualMenuOpen
  const searchInputHeight = '128px'
  const minBottomMargin = '24px'
  const searchInputPadding = '32px'

  return (
    <Box<'div'>
      aria-hidden={!shouldShow}
      className={cn(theme.contextualMenu, shouldShow && theme.show)}
      maxHeight={{
        base: '100vh',
        lg: isMapListNav
          ? '372px'
          : `calc(100vh - ${distanceFromTop}px - ${searchInputHeight} - ${minBottomMargin} + ${searchInputPadding})}`,
      }}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      overflowY={{ lg: 'auto' }}
      role="presentation"
      textAlign="left"
      width={{ base: '100%', lg: '488px' }}
      px={isMobile ? 2 : undefined}
    >
      {isMobile && showMobileAutocomplete && (
        <div className={theme.mobileInputContainer}>
          <AutocompleteMobileBackButton onClick={onClickBack} theme={theme} />
          <AutocompleteFormContainer
            exclude={exclude}
            fillInputOnSelect={fillInputOnSelect}
            handleChange={handleChange}
            onError={onError}
            onSelectAutocompleteResult={onSelectAutocompleteResult}
            placeholder={placeholder}
            resetForm={resetForm}
            searchTerm={formSearchTerm}
            setFieldValue={setFieldValue}
            sholdNotFocusForMobile={sholdNotFocusForMobile}
            showMobileAutocomplete={showMobileAutocomplete}
            theme={theme}
          />
        </div>
      )}
      <Box<'div'>
        aria-label="search results"
        className={theme.inputBtnsContainer}
        id={`${getIdFromOrigin(origin)}-results`}
        onMouseUp={onMouseUpResultLink}
        role="listbox"
        tabIndex={-1}
      >
        <AutocompleteResults
          dispatchMixpanelAutocompleteResultClick={dispatchMixpanelAutocompleteResultClick}
          exclude={exclude}
          fillInputOnSelect={fillInputOnSelect}
          isMapListNav={isMapListNav}
          isShortTerm={isShortTerm}
          onClickGetCurrentPosition={onClickGetCurrentPosition}
          onSelectAutocompleteResult={onSelectAutocompleteResult}
          origin={origin}
          resetForm={resetForm}
          setFieldValue={setFieldValue}
          theme={theme}
        />
      </Box>
      {children && (
        <div className={theme.children} onMouseUp={onMouseUpFooterLink} role="presentation">
          {children}
        </div>
      )}
    </Box>
  )
}

const equalprops = [
  'children',
  'distanceFromTop',
  'exclude',
  'forceContextualMenuOpen',
  'handleChange',
  'hideMobileAutocomplete',
  'onError',
  'onSelectAutocompleteResult',
  'origin',
  'placeholder',
  'resetForm',
  'searchTerm',
  'setFieldValue',
  'showMobileAutocomplete',
] as const

const shallowEqualProps = ['theme'] as const

export const AutocompleteContextualMenu = React.memo(
  AutocompleteContextualMenuComponent,
  createAllowedPropsAreEqual(equalprops, shallowEqualProps)
)
